import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock, ShieldCheck, Maximize, Cpu, Users, Building2, Wrench, Play } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLine, Overline } from "../components/Reveal";
import { AbstractVisual, ParticleField } from "../components/AbstractVisual";
import { api } from "../lib/api";

export default function GameDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setItem(null); setMissing(false);
    api.get("games", slug).then(setItem).catch(() => setMissing(true));
  }, [slug]);

  if (missing) return (
    <main className="pt-48 pb-40 text-center">
      <p className="font-serif text-3xl mb-6">Attraction not found.</p>
      <Link to="/games" className="text-sm text-zinc-400 hover:text-white">← Back to VR Games</Link>
    </main>
  );
  if (!item) return <main className="pt-48 pb-40 text-center text-zinc-500 font-mono text-sm">Loading…</main>;

  const specs = [
    [Clock, "Duration", item.duration], [ShieldCheck, "Recommended Age", item.age],
    [Maximize, "Space Requirement", item.space], [Cpu, "Equipment", item.equipment],
    [Users, "Capacity / Hour", item.capacity], [Building2, "Venue Suitability", item.suitability],
    [Wrench, "Installation", item.installation],
  ];

  return (
    <main>
      <Seo title={item.name} path={`/games/${slug}`} description={item.tagline} />
      <section className="relative min-h-[82vh] flex items-end overflow-hidden" data-testid="game-hero">
        <AbstractVisual hue={item.hue ?? 14} seed={slug.length * 29} />
        <ParticleField hue={item.hue ?? 14} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030305] via-[#030305]/40 to-[#030305]/70" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 pb-24 w-full">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <Link to="/games" data-testid="back-to-games" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-zinc-400 hover:text-white transition-colors mb-10">
              <ArrowLeft size={14} /> VR Games
            </Link>
          </motion.div>
          <h1 className="font-serif tracking-tight leading-[0.98] text-5xl md:text-7xl lg:text-8xl" data-testid="game-title">
            <MaskedLine delay={0.25}>{item.name}</MaskedLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
            className="mt-6 text-lg text-zinc-400 font-light max-w-xl">{item.tagline}</motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75 }} className="mt-9 flex flex-wrap gap-4">
            <Link to="/locations" data-testid="play-at-locations-cta"
              className="px-7 py-3.5 rounded-full bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors inline-flex items-center gap-2">
              <Play size={15} /> Play at Our Locations
            </Link>
            <Link to="/venues" data-testid="install-experience-cta"
              className="px-7 py-3.5 rounded-full border border-white/20 text-sm hover:bg-white/10 transition-colors inline-flex items-center gap-2">
              Install This Experience <ArrowRight size={15} />
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32 border-t border-white/[0.06]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            <Reveal>
              <Overline className="mb-5">The Attraction</Overline>
              <p className="text-lg md:text-xl text-zinc-300 font-light leading-relaxed">{item.description}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl divide-y divide-white/[0.07]" data-testid="game-specs">
                {specs.map(([Icon, label, value]) => (
                  <div key={label} className="flex items-start justify-between gap-6 px-7 py-5">
                    <span className="flex items-center gap-3 text-xs font-mono uppercase tracking-[0.15em] text-zinc-500 shrink-0 pt-0.5"><Icon size={15} />{label}</span>
                    <span className="text-sm text-zinc-200 text-right">{value}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
